/**
 * Time Utilities
 * 
 * Centralized timestamp formatting utilities to ensure consistent
 * timezone handling across the application.
 */

/**
 * Format timestamp for message display with proper UTC handling
 * @param timestamp ISO timestamp string from backend (UTC)
 * @returns Formatted time string in user's local timezone
 */
export function formatMessageTime(timestamp: string): string {
  try {
    // Parse the timestamp - ensure we handle UTC properly
    let date: Date;
    
    // Backend always sends UTC timestamps - parse directly
    // The format is typically: 2025-07-07T15:02:34.351624 (UTC)
    date = new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid timestamp received:', timestamp);
      return 'Invalid time';
    }
    
    
    // Format in user's local timezone
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting timestamp:', timestamp, error);
    return 'Invalid time';
  }
}

/**
 * Format timestamp for detailed display (includes date if not today)
 * @param timestamp ISO timestamp string from backend (UTC)
 * @returns Formatted date/time string
 */
export function formatDetailedTime(timestamp: string): string {
  try {
    let date: Date;
    
    if (timestamp.endsWith('Z') || timestamp.includes('+') || timestamp.includes('T')) {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp + 'Z');
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    }
  } catch (error) {
    console.error('Error formatting detailed timestamp:', timestamp, error);
    return 'Invalid time';
  }
}

/**
 * Get relative time (e.g., "2 minutes ago")
 * @param timestamp ISO timestamp string from backend (UTC)
 * @returns Relative time string
 */
export function getRelativeTime(timestamp: string): string {
  try {
    let date: Date;
    
    if (timestamp.endsWith('Z') || timestamp.includes('+') || timestamp.includes('T')) {
      date = new Date(timestamp);
    } else {
      date = new Date(timestamp + 'Z');
    }
    
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return formatDetailedTime(timestamp);
  } catch (error) {
    console.error('Error calculating relative time:', timestamp, error);
    return 'Invalid time';
  }
}